/**
 * QualityPanel — quality analysis section for a task.
 *
 * Features:
 *   • Star rating (1–5) per reviewer
 *   • Feedback text
 *   • Current user can add / edit their own review
 *   • All reviews listed below with author name + timestamp
 *   • Average rating displayed in header
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../../lib/api';
import { formatRelative } from '../../../lib/format';

// ── Star widget ───────────────────────────────────────────────────────────────
function StarRating({ value, onChange, readonly = false }) {
  const [hover, setHover] = useState(0);
  return (
    <span className="star-rating">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          className={`star-btn ${(hover || value) >= star ? 'filled' : ''}`}
          onClick={readonly ? undefined : () => onChange?.(star)}
          onMouseEnter={readonly ? undefined : () => setHover(star)}
          onMouseLeave={readonly ? undefined : () => setHover(0)}
          disabled={readonly}
          aria-label={`${star} star${star !== 1 ? 's' : ''}`}
        >
          ★
        </button>
      ))}
    </span>
  );
}

const RATING_LABELS = { 1: 'Poor', 2: 'Below average', 3: 'Average', 4: 'Good', 5: 'Excellent' };

// ── Main component ────────────────────────────────────────────────────────────
export default function QualityPanel({ appId, taskId, members, me, taskStatus }) {
  const api = useApi();

  const [reviews, setReviews]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  // My review form state
  const [myRating, setMyRating]     = useState(0);
  const [myFeedback, setMyFeedback] = useState('');
  const [saving, setSaving]         = useState(false);
  const [formError, setFormError]   = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.listQualityReviews(appId, taskId);
      const list = r.items || [];
      setReviews(list);
      // Pre-fill my form if I already have a review
      if (me) {
        const mine = list.find((rev) => rev.reviewer_user_id === me.id);
        if (mine) {
          setMyRating(mine.rating);
          setMyFeedback(mine.feedback || '');
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [api, appId, taskId, me]);

  useEffect(() => { load(); }, [load]);

  const memberName = (id) => {
    if (!id) return 'Unknown';
    const u = (members || []).find((m) => m.user_id === id);
    if (!u) return `User #${id}`;
    return [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (myRating < 1 || myRating > 5) { setFormError('Please select a rating (1–5)'); return; }
    setSaving(true);
    try {
      await api.upsertQualityReview(appId, taskId, {
        rating:   myRating,
        feedback: myFeedback.trim() || null,
      });
      await load();
    } catch (err) {
      setFormError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove your review?')) return;
    try {
      await api.removeQualityReview(appId, taskId, id);
      setMyRating(0);
      setMyFeedback('');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  // Average rating
  const avgRating = reviews.length > 0
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  const myReview = me ? reviews.find((r) => r.reviewer_user_id === me.id) : null;

  return (
    <section className="task-section">
      <div className="quality-header">
        <h2>Quality Analysis</h2>
        {avgRating && (
          <span className="quality-avg-badge">
            ★ {avgRating} <span className="muted small">avg · {reviews.length} review{reviews.length !== 1 ? 's' : ''}</span>
          </span>
        )}
      </div>

      {error && <div className="banner banner-error">{error}</div>}

      {/* ── My review form ── */}
      <div className="quality-my-review">
        <h3>{myReview ? 'Your review' : 'Add your review'}</h3>
        {taskStatus !== 'done' && (
          <div className="banner banner-warn" style={{ marginBottom: 12 }}>
            Quality review can only be submitted after the task is marked as completed.
          </div>
        )}
        <form onSubmit={handleSave} className="quality-form">
          <div className="quality-rating-row">
            <StarRating value={myRating} onChange={taskStatus === 'done' ? setMyRating : undefined} readonly={taskStatus !== 'done'} />
            {myRating > 0 && (
              <span className="quality-rating-label">{RATING_LABELS[myRating]}</span>
            )}
          </div>
          <textarea
            className="form-input"
            rows={3}
            placeholder={taskStatus === 'done' ? 'Add feedback or comments (optional)…' : 'Available once task is completed…'}
            value={myFeedback}
            onChange={(e) => taskStatus === 'done' && setMyFeedback(e.target.value)}
            maxLength={2000}
            disabled={taskStatus !== 'done'}
          />
          {formError && <div className="form-error">{formError}</div>}
          <div className="form-actions" style={{ marginTop: 8 }}>
            {myReview && taskStatus === 'done' && (
              <button
                type="button"
                className="btn-link-sm danger"
                onClick={() => handleDelete(myReview.id)}
                disabled={saving}
              >
                Remove review
              </button>
            )}
            <button
              type="submit"
              className="btn-primary"
              disabled={saving || myRating === 0 || taskStatus !== 'done'}
            >
              {saving ? 'Saving…' : myReview ? 'Update review' : 'Submit review'}
            </button>
          </div>
        </form>
      </div>

      {/* ── All reviews list ── */}
      {loading ? (
        <p className="muted">Loading reviews…</p>
      ) : reviews.length === 0 ? (
        <p className="muted">No reviews yet. Be the first to rate this task.</p>
      ) : (
        <ul className="quality-reviews-list">
          {reviews.map((rev) => (
            <li key={rev.id} className="quality-review-item">
              <div className="quality-review-head">
                <strong>{memberName(rev.reviewer_user_id)}</strong>
                <StarRating value={rev.rating} readonly />
                <span className="quality-rating-label muted small">{RATING_LABELS[rev.rating]}</span>
                <span className="muted small" title={rev.created_at}>
                  · {formatRelative(rev.updated_at || rev.created_at)}
                </span>
              </div>
              {rev.feedback && (
                <p className="quality-review-feedback">{rev.feedback}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
